import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { imgUrl } from '../utils/imgUrl';
import { annualIncomeRanges } from '../data/profileOptions';
import './MyProfile.css';

const MyProfile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authService.getEditProfile()
      .then(res => setProfile(res.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="mp-loading">Loading profile...</div>;
  if (!profile) return null;

  const age = profile.date_of_birth
    ? Math.floor((new Date() - new Date(profile.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000))
    : null;

  const editLink = (tab) => `/edit-profile?tab=${tab.toLowerCase()}`;

  const Section = ({ title, tab, children }) => (
    <div className="mp-section">
      <div className="mp-section-header">
        <h3>{title}</h3>
        <button className="mp-edit-btn" onClick={() => navigate(editLink(tab))}>✏️ Edit</button>
      </div>
      <div className="mp-section-grid">{children}</div>
    </div>
  );

  const Row = ({ label, value }) => (
    <div className="mp-row">
      <span className="mp-label">{label}</span>
      <span className={`mp-value ${!value ? 'mp-empty' : ''}`}>{value || 'Not provided'}</span>
    </div>
  );

  const primaryPhoto = profile.photos?.find(p => p.is_primary);
  const initials = `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`;

  return (
    <div className="mp-wrapper">
      <div className="mp-header">
        <button className="mp-back" onClick={() => navigate('/dashboard')}>← Back</button>
        <h2>My Profile</h2>
        <span />
      </div>

      <div className="mp-hero">
        <div className="mp-avatar" onClick={() => navigate(editLink('photos'))}>
          {primaryPhoto
            ? <img src={imgUrl(primaryPhoto.photo_url)} alt={profile.first_name} />
            : <span>{initials}</span>
          }
          <div className="mp-avatar-overlay">📷</div>
        </div>
        <div className="mp-hero-info">
          <h1>{profile.first_name} {profile.last_name}</h1>
          <p className="mp-profile-id">{profile.profile_id}</p>
          <div className="mp-hero-tags">
            {age && <span>{age} years</span>}
            {profile.height && <span>{profile.height} cm</span>}
            {profile.marital_status && <span>{profile.marital_status.replace('_', ' ')}</span>}
          </div>
        </div>
      </div>

      <div className="mp-body">
        {profile.about_me && (
          <div className="mp-section">
            <div className="mp-section-header">
              <h3>About Me</h3>
              <button className="mp-edit-btn" onClick={() => navigate(editLink('basic'))}>✏️ Edit</button>
            </div>
            <p className="mp-about">{profile.about_me}</p>
          </div>
        )}

        <Section title="Basic Details" tab="basic">
          <Row label="Date of Birth" value={profile.date_of_birth} />
          <Row label="Height" value={profile.height ? `${profile.height} cm` : null} />
          <Row label="Weight" value={profile.weight ? `${profile.weight} kg` : null} />
          <Row label="Marital Status" value={profile.marital_status?.replace('_', ' ')} />
          <Row label="Mother Tongue" value={profile.mother_tongue} />
          <Row label="Physical Status" value={profile.physical_status} />
        </Section>

        <Section title="Religious Details" tab="religious">
          <Row label="Religion" value={profile.religious_details?.religion} />
          <Row label="Caste" value={profile.religious_details?.caste} />
          <Row label="Sub Caste" value={profile.religious_details?.sub_caste} />
          <Row label="Gotra" value={profile.religious_details?.gotra} />
          <Row label="Star" value={profile.religious_details?.star} />
          <Row label="Raasi" value={profile.religious_details?.raasi} />
        </Section>

        <Section title="Professional Details" tab="professional">
          <Row label="Occupation" value={profile.professional_details?.occupation} />
          <Row label="Category" value={profile.professional_details?.occupation_category?.replace(/_/g, ' ')} />
          <Row label="Company" value={profile.professional_details?.company_name} />
          <Row label="Annual Income" value={annualIncomeRanges.find(r => r.value === profile.professional_details?.annual_income)?.label || profile.professional_details?.annual_income} />
          <Row label="Work Location" value={profile.professional_details?.work_location} />
        </Section>

        <Section title="Location" tab="location">
          <Row label="City" value={profile.location?.city} />
          <Row label="State" value={profile.location?.state} />
          <Row label="Country" value={profile.location?.country} />
        </Section>

        <Section title="Family Details" tab="family">
          <Row label="Father's Name" value={profile.family_details?.father_name} />
          <Row label="Father's Occupation" value={profile.family_details?.father_occupation} />
          <Row label="Mother's Name" value={profile.family_details?.mother_name} />
          <Row label="Mother's Occupation" value={profile.family_details?.mother_occupation} />
          <Row label="Siblings" value={profile.family_details?.siblings} />
          <Row label="Family Type" value={profile.family_details?.family_type} />
          <Row label="Family Status" value={profile.family_details?.family_status?.replace(/_/g, ' ')} />
          <Row label="Family Values" value={profile.family_details?.family_values} />
        </Section>

        <Section title="Lifestyle" tab="lifestyle">
          <Row label="Diet" value={profile.lifestyle?.diet} />
          <Row label="Smoking" value={profile.lifestyle?.smoking} />
          <Row label="Drinking" value={profile.lifestyle?.drinking} />
          <Row label="Hobbies" value={profile.lifestyle?.hobbies} />
          <Row label="Interests" value={profile.lifestyle?.interests} />
        </Section>

        <div className="mp-section">
          <div className="mp-section-header">
            <h3>Photos ({profile.photos?.length || 0})</h3>
            <button className="mp-edit-btn" onClick={() => navigate(editLink('photos'))}>✏️ Manage</button>
          </div>
          <div className="mp-photos">
            {profile.photos?.length > 0 ? profile.photos.map(p => (
              <div key={p.id} className={`mp-photo ${p.is_primary ? 'primary' : ''}`}>
                <img src={imgUrl(p.photo_url)} alt="profile" />
                {p.is_primary && <span className="mp-primary-badge">Primary</span>}
                {!p.is_approved && <span className="mp-pending-badge">Pending</span>}
              </div>
            )) : <p className="mp-empty-text">No photos uploaded yet</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;
